import { useEffect, useMemo, useState } from "react";
import {
  Check,
  ChevronDown,
  Search,
  SlidersHorizontal,
  X,
} from "lucide-react";

import { usePermissions } from "../../../../Hooks/usePermissions";
import { MENU } from "../../../../lib/permissions";
import { Button } from "Components/ui/button";
import { Input } from "Components/ui/input";
import { Label } from "Components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "Components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "Components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetBody,
  SheetFooter,
} from "Components/ui/sheet";
import { cn } from "lib/utils";

const TITLE_OPERATORS = [
  { value: "1", label: "contains" },
  { value: "2", label: "is exactly" },
  { value: "3", label: "does not contain" },
  { value: "4", label: "starts with" },
  { value: "5", label: "ends with" },
];

const SALE_PRICE_OPERATORS = [
  { value: ">",  label: "greater than" },
  { value: "<",  label: "less than" },
  { value: "=",  label: "equals" },
  { value: ">=", label: "≥" },
  { value: "<=", label: "≤" },
];

const DEFAULT_FILTERS = {
  title_filter_type: "1",
  title_filter_value: "",
  category: "",
  brand: "",
  occassion: "",
  sale_price_filter_type: ">",
  sale_price_value: "",
  barcode_filter_type: "1",
  barcode_filter_value: "",
};

/**
 * Command-palette style single-select pill. Items are bare divs (not buttons)
 * so the global Bootstrap button reset can't bleed in and give them a
 * default border — that was the "looks like an input box" bug.
 */
function FilterPill({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    if (!open) {
      setQuery("");
      setActiveIndex(0);
    }
  }, [open]);

  const selected = (options || []).find((o) => String(o.value) === String(value));
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options || [];
    return (options || []).filter((o) => o.label.toLowerCase().includes(q));
  }, [query, options]);

  const isActive = !!selected;

  const pick = (next) => {
    onChange(next);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, filtered.length));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex === 0) pick("");
      else pick(String(filtered[activeIndex - 1]?.value ?? ""));
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn("nl-chipfilter", isActive && "is-active")}
          style={{
            borderStyle: isActive ? "solid" : "dashed",
          }}
        >
          <span className="nl-chipfilter__dot nl-chipfilter__dot--neutral" />
          <span style={{ fontWeight: isActive ? 500 : 500 }}>{label}</span>
          {isActive ? (
            <>
              <span
                aria-hidden
                style={{
                  width: 1,
                  height: 12,
                  background: "rgba(255,255,255,0.3)",
                  margin: "0 2px",
                }}
              />
              <span
                style={{
                  maxWidth: 140,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontWeight: 600,
                }}
              >
                {selected.label}
              </span>
            </>
          ) : (
            <ChevronDown size={12} style={{ opacity: 0.5, marginLeft: 2 }} />
          )}
        </button>
      </PopoverTrigger>

      <PopoverContent
        className="w-[260px] overflow-hidden p-0 shadow-xl"
        align="start"
      >
        <div className="flex items-center gap-2 border-b bg-muted/30 px-3 py-2">
          <Search className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          <input
            className="flex-1 bg-transparent text-xs text-foreground outline-none placeholder:text-muted-foreground"
            placeholder={`Search ${label.toLowerCase()}…`}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIndex(0);
            }}
            onKeyDown={onKeyDown}
            autoFocus
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery("")}
              className="rounded-sm border-0 bg-transparent p-0.5 text-muted-foreground hover:bg-accent hover:text-foreground"
              aria-label="Clear search"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div className="max-h-[260px] overflow-y-auto py-1">
          <div
            role="option"
            aria-selected={!isActive}
            onMouseEnter={() => setActiveIndex(0)}
            onClick={() => pick("")}
            className={cn(
              "flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-xs",
              activeIndex === 0 ? "bg-accent text-accent-foreground" : "text-muted-foreground",
              "hover:bg-accent hover:text-accent-foreground"
            )}
          >
            <span>Any {label.toLowerCase()}</span>
            {!isActive && <Check className="h-3.5 w-3.5 text-primary" />}
          </div>

          {filtered.length > 0 && <div className="my-1 h-px bg-border/60" />}

          {filtered.map((opt, idx) => {
            const active = String(opt.value) === String(value);
            const focused = activeIndex === idx + 1;
            return (
              <div
                key={opt.value}
                role="option"
                aria-selected={active}
                onMouseEnter={() => setActiveIndex(idx + 1)}
                onClick={() => pick(String(opt.value))}
                className={cn(
                  "flex cursor-pointer items-center justify-between gap-2 px-3 py-1.5 text-xs",
                  focused
                    ? "bg-accent text-accent-foreground"
                    : "text-foreground",
                  "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                <span className={cn("truncate", active && "font-semibold")}>
                  {opt.label}
                </span>
                {active && <Check className="h-3.5 w-3.5 shrink-0 text-primary" />}
              </div>
            );
          })}

          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-muted-foreground">
              No matches found.
            </div>
          )}
        </div>

        {isActive && (
          <div className="border-t bg-muted/20 p-1">
            <button
              type="button"
              onClick={() => pick("")}
              className="flex w-full items-center justify-center gap-1.5 rounded-sm border-0 bg-transparent px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <X className="h-3 w-3" />
              Clear filter
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

const PendingProductsFilter = ({
  filters,
  onFilterSubmit,
  categoryOptions,
  brandOptions,
}) => {
  const { hasMenuAccess } = usePermissions();
  const canSeeSalePrice = hasMenuAccess(MENU.PRODUCT_SALE_PRICE);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(filters.title_filter_value);
  const [barcodeDraft, setBarcodeDraft] = useState(filters.barcode_filter_value);
  const [advancedDraft, setAdvancedDraft] = useState(filters);

  useEffect(() => {
    setSearchDraft(filters.title_filter_value);
  }, [filters.title_filter_value]);

  useEffect(() => {
    setBarcodeDraft(filters.barcode_filter_value);
  }, [filters.barcode_filter_value]);

  useEffect(() => {
    if (advancedOpen) setAdvancedDraft(filters);
  }, [advancedOpen, filters]);

  const submitSearch = () => {
    onFilterSubmit({ ...filters, title_filter_value: searchDraft });
  };

  const submitBarcode = () => {
    onFilterSubmit({ ...filters, barcode_filter_value: barcodeDraft });
  };

  const setSingle = (key, value) => {
    onFilterSubmit({ ...filters, [key]: value });
  };

  const applyAdvanced = () => {
    onFilterSubmit(advancedDraft);
    setAdvancedOpen(false);
  };

  const resetAll = () => {
    setSearchDraft("");
    onFilterSubmit({ ...DEFAULT_FILTERS });
  };

  const advancedActiveCount =
    (filters.title_filter_type !== DEFAULT_FILTERS.title_filter_type ? 1 : 0) +
    (canSeeSalePrice && filters.sale_price_value ? 1 : 0) +
    (filters.barcode_filter_type !== DEFAULT_FILTERS.barcode_filter_type ? 1 : 0);

  const activeChips = [];
  if (filters.title_filter_value) {
    activeChips.push({
      key: "title_filter_value",
      label: "Title",
      value: filters.title_filter_value,
      clear: () => onFilterSubmit({ ...filters, title_filter_value: "" }),
    });
  }
  if (filters.brand) {
    activeChips.push({
      key: "brand",
      label: "Brand",
      value: filters.brand,
      clear: () => setSingle("brand", ""),
    });
  }
  if (filters.category) {
    activeChips.push({
      key: "category",
      label: "Category",
      value: filters.category,
      clear: () => setSingle("category", ""),
    });
  }
  if (canSeeSalePrice && filters.sale_price_value) {
    activeChips.push({
      key: "sale_price_value",
      label: "Sale price",
      value: `${filters.sale_price_filter_type} ${filters.sale_price_value}`,
      clear: () =>
        onFilterSubmit({
          ...filters,
          sale_price_value: "",
          sale_price_filter_type: DEFAULT_FILTERS.sale_price_filter_type,
        }),
    });
  }
  if (filters.barcode_filter_value) {
    activeChips.push({
      key: "barcode_filter_value",
      label: "Barcode",
      value: filters.barcode_filter_value,
      clear: () =>
        onFilterSubmit({ ...filters, barcode_filter_value: "" }),
    });
  }

  const anyActive = activeChips.length > 0;

  return (
    <div
      style={{
        background: "var(--nb-surface)",
        border: "1px solid var(--nb-border)",
        borderRadius: 12,
        boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
        padding: 12,
      }}
    >
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8 }}>
        <div className="nl-search" style={{ flex: "0 1 320px", minWidth: 220 }}>
          <Search size={14} className="nl-search__icon" aria-hidden="true" />
          <input
            placeholder="Search by title…"
            value={searchDraft}
            onChange={(e) => setSearchDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitSearch();
            }}
            onBlur={() => {
              if (searchDraft !== filters.title_filter_value) submitSearch();
            }}
          />
          {searchDraft && (
            <button
              type="button"
              className="nl-search__clear"
              onClick={() => {
                setSearchDraft("");
                onFilterSubmit({ ...filters, title_filter_value: "" });
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="nl-search" style={{ flex: "0 1 256px", minWidth: 176 }}>
          <Search size={14} className="nl-search__icon" aria-hidden="true" />
          <input
            placeholder="Search by barcode…"
            value={barcodeDraft}
            onChange={(e) => setBarcodeDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitBarcode();
            }}
            onBlur={() => {
              if (barcodeDraft !== filters.barcode_filter_value) submitBarcode();
            }}
          />
          {barcodeDraft && (
            <button
              type="button"
              className="nl-search__clear"
              onClick={() => {
                setBarcodeDraft("");
                onFilterSubmit({ ...filters, barcode_filter_value: "" });
              }}
              aria-label="Clear barcode search"
            >
              ×
            </button>
          )}
        </div>

        <span
          aria-hidden
          style={{ width: 1, height: 22, background: "var(--nb-border)" }}
        />

        <FilterPill
          label="Brand"
          value={filters.brand}
          options={brandOptions}
          onChange={(v) => setSingle("brand", v)}
        />
        <FilterPill
          label="Category"
          value={filters.category}
          options={categoryOptions}
          onChange={(v) => setSingle("category", v)}
        />

        <Sheet open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <button
            type="button"
            className="nb-btn nb-btn--ghost"
            onClick={() => setAdvancedOpen(true)}
            style={{ borderRadius: 999, height: 30, padding: "0 12px" }}
          >
            <SlidersHorizontal size={13} aria-hidden="true" />
            More filters
            {advancedActiveCount > 0 && (
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 16,
                  height: 16,
                  padding: "0 5px",
                  borderRadius: 999,
                  background: "var(--nb-primary)",
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                  marginLeft: 2,
                }}
              >
                {advancedActiveCount}
              </span>
            )}
          </button>

          <SheetContent side="right">
            <SheetHeader>
              <SheetTitle>More filters</SheetTitle>
              <SheetDescription>
                Operator-shaped fields that don't fit a single-select pill.
                Changes apply when you click Apply.
              </SheetDescription>
            </SheetHeader>

            <SheetBody className="space-y-5">
              <div className="space-y-1.5">
                <Label htmlFor="adv-title">Title operator</Label>
                <div className="flex gap-2">
                  <Select
                    value={advancedDraft.title_filter_type}
                    onValueChange={(v) =>
                      setAdvancedDraft((p) => ({ ...p, title_filter_type: v }))
                    }
                  >
                    <SelectTrigger className="w-44 shrink-0">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TITLE_OPERATORS.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Input
                    id="adv-title"
                    value={advancedDraft.title_filter_value}
                    onChange={(e) =>
                      setAdvancedDraft((p) => ({ ...p, title_filter_value: e.target.value }))
                    }
                    placeholder="Title query…"
                  />
                </div>
                <p className="text-xs text-muted-foreground">
                  Override the default `contains` mode for the toolbar search.
                </p>
              </div>

              {canSeeSalePrice && (
                <div className="space-y-1.5">
                  <Label htmlFor="adv-sale-price">Sale price</Label>
                  <div className="flex gap-2">
                    <Select
                      value={advancedDraft.sale_price_filter_type}
                      onValueChange={(v) =>
                        setAdvancedDraft((p) => ({ ...p, sale_price_filter_type: v }))
                      }
                    >
                      <SelectTrigger className="w-44 shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SALE_PRICE_OPERATORS.map((op) => (
                          <SelectItem key={op.value} value={op.value}>
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      id="adv-sale-price"
                      type="number"
                      inputMode="decimal"
                      value={advancedDraft.sale_price_value}
                      onChange={(e) =>
                        setAdvancedDraft((p) => ({ ...p, sale_price_value: e.target.value }))
                      }
                      placeholder="Amount"
                    />
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="adv-barcode">Barcode operator</Label>
                <Select
                  value={advancedDraft.barcode_filter_type}
                  onValueChange={(v) =>
                    setAdvancedDraft((p) => ({ ...p, barcode_filter_type: v }))
                  }
                >
                  <SelectTrigger id="adv-barcode" className="w-44 shrink-0">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TITLE_OPERATORS.map((op) => (
                      <SelectItem key={op.value} value={op.value}>
                        {op.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Override the default `contains` mode for the toolbar barcode search.
                </p>
              </div>
            </SheetBody>

            <SheetFooter>
              <Button variant="outline" onClick={() => setAdvancedOpen(false)}>
                Cancel
              </Button>
              <Button onClick={applyAdvanced}>Apply</Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>

        {anyActive && (
          <button
            type="button"
            onClick={resetAll}
            style={{
              marginLeft: "auto",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              height: 30,
              padding: "0 10px",
              border: "none",
              background: "transparent",
              color: "var(--nb-muted)",
              fontSize: 12,
              fontWeight: 500,
              borderRadius: 7,
              cursor: "pointer",
              fontFamily: "inherit",
            }}
          >
            <X size={13} aria-hidden="true" />
            Clear all
          </button>
        )}
      </div>

      {anyActive && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 6,
            marginTop: 12,
            paddingTop: 12,
            borderTop: "1px solid var(--nb-border)",
          }}
        >
          <span
            style={{
              fontSize: 10.5,
              fontWeight: 600,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--nb-subtle)",
            }}
          >
            Active
          </span>
          {activeChips.map((chip) => (
            <button
              key={chip.key}
              type="button"
              onClick={chip.clear}
              aria-label={`Clear ${chip.label}`}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                height: 24,
                padding: "0 6px 0 10px",
                border: "1px solid rgba(15, 23, 42, 0.1)",
                borderRadius: 6,
                background: "#fff",
                fontSize: 11.5,
                color: "var(--nb-text)",
                cursor: "pointer",
                fontFamily: "inherit",
                fontVariantNumeric: "tabular-nums",
                maxWidth: 240,
                transition: "background 0.12s ease, border-color 0.12s ease, color 0.12s ease",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = "#fef2f2";
                e.currentTarget.style.borderColor = "#fecaca";
                e.currentTarget.style.color = "#b91c1c";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "#fff";
                e.currentTarget.style.borderColor = "rgba(15, 23, 42, 0.1)";
                e.currentTarget.style.color = "var(--nb-text)";
              }}
            >
              <span
                style={{
                  fontWeight: 600,
                  fontSize: 10.5,
                  letterSpacing: "0.02em",
                  textTransform: "uppercase",
                  color: "var(--nb-muted)",
                }}
              >
                {chip.label}
              </span>
              <span
                style={{
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  maxWidth: 140,
                }}
              >
                {chip.value}
              </span>
              <X
                size={12}
                aria-hidden="true"
                style={{ color: "var(--nb-subtle)", marginLeft: 2 }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default PendingProductsFilter;
